import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowSquareOut, LinkSimple, LinkSimpleBreak, LockSimple, Plus, Truck } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const STATE = {
  Reserved: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  "In stock": "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
  "On order": "border-amber-500/40 bg-amber-500/10 text-amber-300",
  "Needs ordering": "border-red-500/40 bg-red-500/10 text-red-300",
};
const PO_TONE = {
  Received: "text-emerald-300",
  Partial: "text-amber-300",
  Ordered: "text-indigo-300",
  Draft: "text-zinc-400",
};

const PoRow = ({ po, projectId, onChange, showColors }) => {
  const toggle = async () => {
    try {
      const { data } = po.linked
        ? await api.delete(`/projects/${projectId}/pos/${po.id}`)
        : await api.post(`/projects/${projectId}/pos/${po.id}`);
      onChange(data);
      toast.success(po.linked ? `${po.code} unlinked` : `${po.code} linked to this project`);
    } catch (e) {
      toast.error("Could not update the PO link");
    }
  };
  return (
    <div
      data-testid={`material-po-${po.id}`}
      data-linked={po.linked ? "1" : "0"}
      className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs"
    >
      <Link
        to={`/inventory/purchase-orders/${po.id}`}
        data-testid={`material-po-link-${po.id}`}
        className="font-mono-data font-semibold text-indigo-300 hover:text-indigo-200"
      >
        {po.code}
      </Link>
      <span className={`font-bold uppercase tracking-widest text-[10px] ${PO_TONE[po.status] || "text-zinc-400"}`}>
        {po.status}
      </span>
      <span className="min-w-0 truncate text-zinc-400">{po.supplier_name || "No supplier"}</span>
      {showColors && po.colors?.length > 0 && (
        <span className="truncate text-zinc-500">{po.colors.join(", ")}</span>
      )}
      <span className="font-mono-data text-zinc-500">
        {po.received_qty}/{po.qty} rec'd
        {po.expected_date ? ` · ETA ${po.expected_date}` : ""}
      </span>
      <button
        type="button"
        data-testid={`material-po-toggle-${po.id}`}
        onClick={toggle}
        className={`ml-auto inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
          po.linked
            ? "border-zinc-700 text-zinc-400 hover:border-red-500/50 hover:text-red-300"
            : "border-indigo-500/40 bg-indigo-500/10 text-indigo-200 hover:bg-indigo-500/20"
        }`}
      >
        {po.linked ? <LinkSimpleBreak size={11} weight="bold" /> : <LinkSimple size={11} weight="bold" />}
        {po.linked ? "Unlink" : "Link PO"}
      </button>
    </div>
  );
};

// Per-colour material status for the project + the POs that carry that stone.
export const ProjectMaterialNeeds = ({ project, needs, onChange }) => {
  const [reserving, setReserving] = useState(false);
  if (!needs) return null;
  const accepted = (project.quotes || []).some((q) => ["Accepted", "Completed"].includes(q.status));
  const reserveNow = async () => {
    setReserving(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/reserve-material`);
      onChange(data);
      toast.success(
        data.reserved ? `${data.reserved} slab${data.reserved > 1 ? "s" : ""} reserved for this project` : "Nothing in stock to reserve",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reserve material");
    } finally {
      setReserving(false);
    }
  };
  const newPoHref = (color) =>
    `/inventory/purchase-orders/new?project=${project.id}&slab_color=${encodeURIComponent(color)}` +
    `${project.customer_id ? `&customer=${project.customer_id}` : ""}` +
    `${project.jobs?.[0]?.id ? `&job=${project.jobs[0].id}` : ""}`;
  const otherLinked = (needs.linked_pos || []).filter(
    (po) => !(needs.needs || []).some((n) => n.pos.some((p) => p.id === po.id)),
  );
  return (
    <div data-testid="project-material-needs" className="mt-4 space-y-3">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
        <Truck size={13} weight="bold" /> Material needs
        {needs.needs_ordering > 0 && (
          <span data-testid="material-needs-ordering-count" className="rounded-full bg-red-500/20 px-1.5 text-red-300">
            {needs.needs_ordering} to order
          </span>
        )}
        {needs.on_order > 0 && (
          <span data-testid="material-on-order-count" className="rounded-full bg-amber-500/20 px-1.5 text-amber-300">
            {needs.on_order} on order
          </span>
        )}
      </div>
      {(needs.needs || []).length === 0 && (
        <p data-testid="material-needs-empty" className="text-xs text-zinc-500">
          No stone color on the quote yet — pick a material in the Design Studio to see what needs ordering.
        </p>
      )}
      {(needs.needs || []).map((n) => (
        <div
          key={n.color}
          data-testid={`material-need-${n.color.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          data-state={n.state}
          className="rounded-lg border border-zinc-800 bg-zinc-950 p-3"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={
                n.slab_ids?.length === 1
                  ? `/inventory/slabs/${n.slab_ids[0]}`
                  : `/inventory/slabs?q=${encodeURIComponent(n.color)}`
              }
              data-testid={`material-item-link-${n.color.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="text-sm font-semibold text-zinc-100 underline-offset-2 hover:text-indigo-200 hover:underline"
            >
              {n.color}
            </Link>
            <span className="font-mono-data text-xs text-zinc-500">
              {n.sqft} ft² · {n.quotes.join(", ")}
            </span>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATE[n.state]}`}>
              {n.state}
              {n.state === "Reserved" && n.reserved.length ? ` · ${n.reserved.join(", ")}` : ""}
              {n.state === "In stock" ? ` · ${n.in_stock} slab${n.in_stock === 1 ? "" : "s"}` : ""}
            </span>
            <span className="ml-auto flex items-center gap-2">
              {n.state === "In stock" && accepted && (
                <button
                  type="button"
                  data-testid={`material-reserve-now-${n.color.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  disabled={reserving}
                  onClick={reserveNow}
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 text-[10px] font-bold uppercase tracking-widest text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                >
                  <LockSimple size={11} weight="bold" /> {reserving ? "Reserving…" : "Reserve now"}
                </button>
              )}
              {n.state === "In stock" && (
                <Link
                  to={
                    n.slab_ids?.length === 1
                      ? `/inventory/slabs/${n.slab_ids[0]}`
                      : `/inventory/slabs?q=${encodeURIComponent(n.color)}`
                  }
                  data-testid={`material-pick-${n.color.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-indigo-500/40 px-2.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/10"
                >
                  Pick slab <ArrowSquareOut size={11} weight="bold" />
                </Link>
              )}
              {n.state !== "Reserved" && (
                <Link
                  to={newPoHref(n.color)}
                  data-testid={`material-new-po-${n.color.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-zinc-700 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-500 hover:text-indigo-200"
                >
                  <Plus size={11} weight="bold" /> New PO
                </Link>
              )}
            </span>
          </div>
          {n.pos.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {n.pos.map((po) => (
                <PoRow key={po.id} po={po} projectId={project.id} onChange={onChange} />
              ))}
            </div>
          )}
          {n.state === "Needs ordering" && n.pos.length === 0 && (
            <p className="mt-2 text-xs text-zinc-500">
              Nothing in stock and no purchase order carries this color yet.
            </p>
          )}
        </div>
      ))}
      {otherLinked.length > 0 && (
        <div data-testid="material-linked-other">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Other linked POs
          </div>
          <div className="space-y-1.5">
            {otherLinked.map((po) => (
              <PoRow key={po.id} po={po} projectId={project.id} onChange={onChange} showColors />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
